/**
 * ionDemoMode — client-only Sales-Selection demo REPLAY.
 *
 * The real backend (apply_application_profile / rank_cells_for_application +
 * the branch knowledge profiles) is now live, so the live `/api/v1/chat/stream`
 * path is the DEFAULT for every user. This scripted replay is kept only as an
 * opt-in sales hedge (deterministic, zero token cost, never hallucinates on
 * stage) — it is NOT the product and must never shadow the live flow for a
 * normal user.
 *
 * Activation requires BOTH:
 *   1. the URL flag `?ion_demo=power_tool`, AND
 *   2. an explicit opt-in: `localStorage['ion_demo_replay'] === '1'`
 *      (set it from the console or via setDemoReplayEnabled(true) before a
 *      sales call). Without the opt-in the param is inert and the live
 *      backend answers — so a trial user who stumbles onto the URL still
 *      gets the real product.
 *
 * When active, useChatStream.sendChatMessage short-circuits the network fetch
 * and streams a local NDJSON fixture line-by-line through the SAME dispatch
 * path the real backend uses.
 */

// `?raw` imports bake the fixture text into the bundle. msw itself is NOT
// pulled in, so this works in the production / staging build where the
// MSW dev-only handler isn't registered.
import powerToolFixture from '../../../mocks/sales-selection-power-tool.ndjson?raw'

const BRANCH_FIXTURES = {
  power_tool: powerToolFixture,
  // Add ebike / oem_automotive / bess / drone fixtures here when their
  // walkthrough scripts are written (F1 scope: power_tool only).
}

const DEMO_PARAM = 'ion_demo'
const DEMO_OPT_IN_KEY = 'ion_demo_replay'

/**
 * Explicit opt-in gate. The scripted replay only runs when an operator has
 * deliberately enabled it on this device — so the live backend is the default
 * for everyone else, even if they land on a `?ion_demo=` URL.
 */
export function isDemoReplayEnabled() {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(DEMO_OPT_IN_KEY) === '1'
    )
  } catch {
    return false
  }
}

/** Toggle the sales-replay opt-in (console / sales-call helper). */
export function setDemoReplayEnabled(enabled) {
  try {
    if (enabled) localStorage.setItem(DEMO_OPT_IN_KEY, '1')
    else localStorage.removeItem(DEMO_OPT_IN_KEY)
  } catch {
    /* localStorage unavailable — no-op */
  }
}

/**
 * Read the demo branch from the current URL. Returns the branch slug
 * (e.g. 'power_tool') or null if the URL carries no valid demo flag.
 * NOTE: this is pure URL parsing — it does NOT consult the opt-in gate.
 * Use isDemoActive() to decide whether to actually replay.
 */
export function getDemoBranch() {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const param = url.searchParams.get(DEMO_PARAM)
    if (param && Object.prototype.hasOwnProperty.call(BRANCH_FIXTURES, param)) {
      return param
    }
  } catch {
    /* malformed URL — fall through */
  }
  return null
}

/**
 * Convenience predicate. True only when BOTH the URL flag is present AND the
 * operator has opted in (localStorage) — otherwise the live backend handles
 * the turn. This is the gate sendChatMessage checks.
 */
export function isDemoActive() {
  return getDemoBranch() !== null && isDemoReplayEnabled()
}

/**
 * Append the demo flag to a target path while preserving any existing
 * query / hash. Pass-through if no demo is active.
 *
 *   appendDemoParam('/assistant') → '/assistant?ion_demo=power_tool'
 *   appendDemoParam('/cells?a=1') → '/cells?a=1&ion_demo=power_tool'
 */
export function appendDemoParam(targetPath) {
  const branch = getDemoBranch()
  if (!branch || !targetPath) return targetPath
  // Use a relative-safe URL constructor (the `base` is irrelevant — we
  // only care about path + search manipulation).
  try {
    const u = new URL(targetPath, 'http://x')
    u.searchParams.set(DEMO_PARAM, branch)
    return u.pathname + u.search + u.hash
  } catch {
    return targetPath
  }
}

/**
 * Async generator that yields parsed NDJSON events from the active demo
 * fixture, with small randomised delays between frames to feel like a
 * real stream. Throws if no fixture matches the active demo flag.
 */
export async function* streamDemoEvents({ minDelayMs = 25, maxDelayMs = 90 } = {}) {
  const branch = getDemoBranch()
  if (!branch) throw new Error('streamDemoEvents called outside demo mode')

  const ndjson = BRANCH_FIXTURES[branch]
  const lines = ndjson.split('\n').filter((l) => l.trim().length > 0)

  for (const line of lines) {
    const delay = Math.floor(Math.random() * (maxDelayMs - minDelayMs + 1)) + minDelayMs
    await new Promise((resolve) => setTimeout(resolve, delay))
    try {
      yield JSON.parse(line)
    } catch (e) {
      console.warn('[ionDemoMode] skipping malformed fixture line:', e)
    }
  }
}

// Exported for tests.
export const __test__ = { BRANCH_FIXTURES, DEMO_PARAM }
